import React from 'react';
import styles from './RoboKidsLanding.module.css';

export default function RoboKidsLanding() {
  // ---- Class Offerings ----
  const classOfferings = [
    {
      id: 'basic',
      title: 'Basic: AI Robotics Foundations',
      description:
        "Start with the building blocks of AI robotics: circuits, motors, sensors, and simple decision logic. You'll build robots that respond to their environment.",
      image: '/images/robokids1.png',
      schedule: ['Saturday, July 18', 'Saturday, July 25', 'Saturday, August 1', 'Saturday, August 8'],
    },
    {
      id: 'advanced',
      title: 'Advanced: Autonomous AI Robotics Mastery',
      description:
        'Master autonomy: line-following, obstacle-avoiding, and decision-tree style programming. Build robots that seem to "think" with AI logic.',
      image: '/images/robokids2.png',
      schedule: ['Saturday, July 18', 'Saturday, July 25', 'Saturday, August 1', 'Saturday, August 8'],
    },
    {
      id: 'camp',
      title: 'Science Camp: Multi-Day STEM Experience',
      description:
        'An intensive hands-on camp where you build multiple robots, explore electronics deeper, and collaborate on team challenges.',
      image: '/images/robokids3.png',
      schedule: ['Summer 2026', 'Fall 2026 (dates coming soon)'],
    },
  ];

  // ---- Testimonials ----
  const testimonials = [
    {
      id: 'parent1',
      name: 'Aditi S.',
      location: 'Fremont',
      image: '/images/parent1.png',
      quote:
        "My son went from zero experience to building a working obstacle-avoidance robot in just one day. He can't wait for the next workshop!",
    },
    {
      id: 'parent2',
      name: 'Gerald M.',
      location: 'San Jose',
      image: '/images/parent2.png',
      quote:
        'The instructor made everything hands-on and practical. My daughter learned how to wire motors, read sensors, and think like an engineer.',
    },
    {
      id: 'parent3',
      name: 'Chen-Yan R.',
      location: 'Milpitas',
      image: '/images/parent3.png',
      quote:
        'Best STEM experience we\'ve had. The kids learn real skills, not just theory. This is how engineering should be taught.',
    },
  ];

  const eventbriteUrl = 'https://www.eventbrite.com/e/ai-robotics-workshop-for-teens-hands-on-engineering-experience-weekly-tickets-1990292587707';

  return (
    <div className={styles.landingContainer}>
      {/* Header */}
      <header className={styles.programHeader} role="banner">
        <h1 className={styles.programTitle}>Live AI Robotics Workshops for Teens</h1>
        <p className={styles.programSubtitle}>
          Every Saturday • 9:30 AM – 12:00 PM • Ages 13+ • No experience needed
        </p>
        <p className={styles.programDescription}>
          Build working robots, learn electronics and Arduino coding, and take your creation home.
          Led by engineers who build real projects.
        </p>
      </header>

      {/* Class Offerings Grid - MOVED TO TOP */}
      <section className={styles.cardGrid} aria-label="Class offerings">
        {classOfferings.map((offering) => (
          <article className={styles.classCard} key={offering.id}>
            <img
              src={offering.image}
              alt={`${offering.title} course illustration`}
              className={styles.cardImage}
              loading="lazy"
            />
            <h2 className={styles.cardTitle}>{offering.title}</h2>
            <p className={styles.cardDescription}>{offering.description}</p>

            <div className={styles.scheduleSection}>
              <strong className={styles.scheduleHeading}>Upcoming Dates:</strong>
              <ul className={styles.scheduleList}>
                {offering.schedule.map((date, index) => (
                  <li key={`${offering.id}-date-${index}`} className={styles.scheduleItem}>
                    {date}
                  </li>
                ))}
              </ul>
            </div>

            <a
              href={eventbriteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.viewButton}
              aria-label={`Sign up for ${offering.title} on Eventbrite`}
            >
              Sign Up Now
            </a>
          </article>
        ))}
      </section>

      {/* Main CTA Banner - COMMENTED OUT
      <div className={styles.ctaBanner}>
        <h2>Ready to Build Your First Robot?</h2>
        <a 
          href={eventbriteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.primaryCtaButton}
        >
          Sign Up on Eventbrite
        </a>
        <p className={styles.ctaSubtext}>
          Individual admission available • Bring a sibling and save (see below)
        </p>
      </div>
      */}

      {/* Sibling Promotion Banner - MOVED BELOW CARDS */}
      <section className={styles.siblingPromoBanner}>
        <div className={styles.siblingPromoContent}>
          <h3 className={styles.siblingPromoTitle}>🎁 Special Offer: Sibling Admission</h3>
          <p className={styles.siblingPromoPrice}>
            <strong>$65.87</strong> <span className={styles.feeNote}>(incl. $5.87 fee)</span>
          </p>
          <p className={styles.siblingPromoDescription}>
            Bring two siblings from the same household. Both students participate together, 
            sharing one engineering starter kit and one workstation during the workshop.
          </p>
          <a 
            href={eventbriteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.siblingPromoButton}
          >
            Get Sibling Ticket on Eventbrite
          </a>
        </div>
      </section>

      {/* More Dates Note */}
      <p className={styles.moreDatesGlobalText}>
        More dates coming as workshops fill up. New cohorts will be added throughout the week.
      </p>

      {/* Testimonials */}
      <section className={styles.testimonials} aria-label="Parent testimonials">
        <h2 className={styles.testimonialsHeading}>What Parents & Students Say</h2>
        <div className={styles.testimonialsGrid}>
          {testimonials.map((testimonial) => (
            <figure className={styles.testimonialCard} key={testimonial.id}>
              <img
                src={testimonial.image}
                alt={`${testimonial.name} testimonial`}
                className={styles.testimonialImage}
                loading="lazy"
              />
              <blockquote className={styles.testimonialQuote}>
                {testimonial.quote}
              </blockquote>
              <figcaption className={styles.testimonialAuthor}>
                — {testimonial.name}, {testimonial.location}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className={styles.whyChooseUs}>
        <h2 className={styles.whyHeading}>Why Parents Choose ChronoTech Robotics</h2>
        <ul className={styles.whyList}>
          <li>Ages 13+ — beginner to advanced • No prior experience needed</li>
          <li>Live Saturday instruction — serving San Jose, Fremont, Milpitas, and the Bay Area</li>
          <li>Hands-on with real robots: sensors, motors, Arduino microcontrollers</li>
          <li>STEM projects: obstacle avoidance, line following, coding logic</li>
          <li>
            <strong>Sibling Special:</strong> Two kids join for $65.87 (share one kit & workstation)
          </li>
        </ul>
      </section>

      {/* SEO Footer */}
      <footer
        style={{
          fontSize: '12px',
          color: '#888',
          marginTop: '40px',
          textAlign: 'center',
          paddingBottom: '20px',
        }}
      >
        AI Robotics workshops for teens in San Jose, Fremont, Milpitas, Santa Clara, and across
        the Bay Area. Hands-on STEM education with real robots, Arduino programming, and electronics.
        Build obstacle-avoidance robots, line-following bots, and sensor systems. Beginner-friendly,
        ages 13+. Weekly Saturday workshops. Sibling admission available.
      </footer>
    </div>
  );
}